import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Button,
  Card, CardContent, Checkbox, FormControlLabel, Grid, Tab,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { SOIL_MOISTURE_NAME } from '../../../../constants/graphs';
import useFetchApi from '../../../../hooks/useFetchApi';
import apiClient from '../../../../requests/api/apiClient';
import {
  GetSectorSoilMoistureConfigResponse,
  GetSoilMoistureAvailableDepthsResponse,
} from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { FetchError } from '../../../../views/types';
import AlertModal from '../../AlertModal/AlertModal';
import AlertsTab from '../../AlertsTab/AlertsTab';
import { ALERT_LIST_UI } from '../../AlertsTab/constants';
import ConversationsTab from '../../ConversationTab';
import useStyles from '../../styles';
import PhaseLineManager from './PhaseLineManager';
import { CriticalPoint } from './types';
import { validatePoints } from './utils';
import { ModalState, EMPTY_MODAL_STATE } from '../../chartModal';
import useIsClientOperator from '../../../../hooks/useIsClientOperator';

interface SoilMoistureConfigContentProps {
  sectorId: string;
  setModalState?: React.Dispatch<React.SetStateAction<ModalState>>;
  refreshCounter?: number;
  onConfigSaved?: () => void;
}

const SoilMoistureConfigContent = ({
  sectorId,
  setModalState: setModalStateProp,
  refreshCounter = 0,
  onConfigSaved,
}: SoilMoistureConfigContentProps) => {
  const classes = useStyles();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const { enqueueSnackbar } = useSnackbar();
  const { interval } = useSelector((state: ReduxState) => state.graphsFilter);

  // Default to conversations tab when arriving from a notification (date param).
  const date = queryParams.get('date');
  const initialTabValue = date ? '3' : '1';

  const [tabValue, setTabValue] = useState(initialTabValue);
  const [availableDepths, setAvailableDepths] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertsRefreshCounter, setAlertsRefreshCounter] = useState(0);
  const [, setChartRefreshCounter] = useState(0);

  const [, setInternalModalState] = useState<ModalState>(EMPTY_MODAL_STATE);
  const setModalState = setModalStateProp ?? setInternalModalState;

  const [criticalPoints, setCriticalPoints] = useState<CriticalPoint[]>([
    { id: 6, label: 'maxValue', customLabel: 'Humedad Máxima' },
    { id: 1, label: 'criticalPoint1', customLabel: 'Capacidad de Campo Sonda' },
    { id: 2, label: 'criticalPoint2', customLabel: 'Alto Potencial Hídrico' },
    { id: 3, label: 'criticalPoint3', customLabel: 'Bajo Potencial Hídrico' },
    { id: 4, label: 'criticalPoint4', customLabel: 'Cambio de Potencial' },
    { id: 5, label: 'minValue', customLabel: 'Humedad Mínima' },
  ]);

  const isClientOperator = useIsClientOperator();

  useEffect(() => {
    if (isClientOperator && tabValue === '1') {
      setTabValue('2');
    }
  }, [isClientOperator, tabValue]);

  const {
    data: fetchedAvailableDepths,
    loading: availableDepthsLoading,
  } = useFetchApi<GetSoilMoistureAvailableDepthsResponse>({
    modelEndpoint: 'availableDepthsDetail',
    modelName: 'soilMoistureConfig',
    id: sectorId,
    dependencies: [sectorId],
  });

  const {
    data: soilMoistureConfig,
    loading: soilMoistureConfigLoading,
  } = useFetchApi<GetSectorSoilMoistureConfigResponse>({
    modelEndpoint: 'soilMoistureConfig',
    modelName: 'soilMoistureConfig',
    id: sectorId,
    dependencies: [sectorId],
  });

  useEffect(() => {
    // Depths depend only on sectorId (the fetch is keyed on it), not on the
    // selected interval/sector — gating on those breaks the dashboard dialog
    // where redux interval/selectedSector are not set.
    if (availableDepthsLoading) return;
    setAvailableDepths(fetchedAvailableDepths?.availableDepths || []);
  }, [availableDepthsLoading, fetchedAvailableDepths]);

  useEffect(() => {
    if (!soilMoistureConfigLoading) {
      const chartConfig = soilMoistureConfig?.soilMoistureConfig;
      const generalLines = chartConfig?.generalLines || [];
      setSelectedLevels(chartConfig?.levels || []);
      if (generalLines.length > 0) {
        setCriticalPoints(generalLines);
      }
    }
  }, [soilMoistureConfigLoading]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleLevelChange = async (newLevel: string) => {
    const newLevels = selectedLevels.includes(newLevel)
      ? selectedLevels.filter((level) => level !== newLevel)
      : [...selectedLevels, newLevel];

    setSelectedLevels(newLevels);

    try {
      const { data: response } = await apiClient.soilMoistureConfig.levelsCreate(
        sectorId,
        { levels: newLevels },
      );
      enqueueSnackbar(response.message, { variant: 'success' });
      onConfigSaved?.();
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleCriticalPointChange = (id: number, value: number) => {
    const pointValue = value !== 0 ? value : undefined;
    setCriticalPoints(criticalPoints.map((point) => (
      point.id === id ? { ...point, value: pointValue } : point
    )));
  };

  const handleCustomLabelChange = (id: number, newLabel: string) => {
    setCriticalPoints(criticalPoints.map((point) => (
      point.id === id ? { ...point, customLabel: newLabel } : point
    )));
  };

  const updateGeneralLines = async () => {
    const currentMax = criticalPoints.find((p) => p.id === 6)?.value ?? 0;
    const currentMin = criticalPoints.find((p) => p.id === 5)?.value ?? 0;
    if (!validatePoints(criticalPoints, enqueueSnackbar, currentMin, currentMax)) {
      return;
    }

    try {
      const { data: response } = await apiClient.soilMoistureConfig.generalLinesCreate(
        sectorId, { generalLines: criticalPoints },
      );
      enqueueSnackbar(response.message, { variant: 'success' });
      setChartRefreshCounter((prev) => prev + 1);
      onConfigSaved?.();
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const generalLinesForPhaseManager = useMemo(
    () => soilMoistureConfig?.soilMoistureConfig?.generalLines || [],
    [soilMoistureConfig],
  );

  return (
    <>
      <TabContext value={tabValue}>
        <Box sx={{
          borderBottom: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <TabList onChange={handleTabChange} aria-label="sector tabs" className={classes.tabList}>
            {!isClientOperator && (
              <Tab label="Configuración" value="1" className={classes.tabLabel} />
            )}
            <Tab label="Alertas" value="2" className={classes.tabLabel} />
            <Tab label="Conversaciones" value="3" className={classes.tabLabel} />
          </TabList>
          {tabValue === '2' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setAlertModalOpen(true)}
              sx={{ ml: 2 }}
            >
              Crear Alerta
            </Button>
          )}
        </Box>
        {!isClientOperator && (
          <TabPanel value="1" className={classes.configTabPanel}>
            <Grid container spacing={3} sx={{ width: '100%' }}>
              <Grid item xs={4}>
                <Card>
                  <CardContent>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Niveles a utilizar
                      </Typography>
                      {availableDepths.map((option) => (
                        <FormControlLabel
                          key={`${option}-checkbox`}
                          sx={{ display: 'block', mt: 1 }}
                          control={
                            <Checkbox
                              name={option}
                              checked={selectedLevels.includes(option)}
                              onChange={() => handleLevelChange(option)}
                            />
                          }
                          label={option}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <Card>
                  <CardContent>
                    <Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom mb={2}>
                        Configurar Líneas de Gestión Generales
                      </Typography>
                      <Grid container spacing={2}>
                        {criticalPoints.map((point) => (
                          <Grid
                            key={point.id}
                            item
                            xs={12}
                            container
                            alignItems="center"
                            spacing={2}
                          >
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                type="number"
                                label={point.label}
                                value={point.value ?? ''}
                                onChange={(e) => handleCriticalPointChange(
                                  point.id,
                                  Number(e.target.value),
                                )}
                                variant="standard"
                              />
                            </Grid>
                            <Grid item xs={8}>
                              <TextField
                                fullWidth
                                label="Etiqueta"
                                value={point.customLabel}
                                onChange={(e) => handleCustomLabelChange(
                                  point.id, e.target.value,
                                )}
                                variant="standard"
                              />
                            </Grid>
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={updateGeneralLines}
                          >
                            Actualizar Líneas de Gestión Generales
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={4}>
                <PhaseLineManager
                  generalLines={generalLinesForPhaseManager}
                  onPhaseLinesUpdated={() => {
                    setChartRefreshCounter((prev) => prev + 1);
                    onConfigSaved?.();
                  }}
                />
              </Grid>
            </Grid>
          </TabPanel>
        )}
        <TabPanel value="2" className={classes.configTabPanel}>
          <Box sx={{ position: 'relative', minHeight: '200px' }}>
            <AlertsTab
              sectorId={sectorId}
              headers={ALERT_LIST_UI}
              refreshCounter={alertsRefreshCounter}
              chartName={'humedad-suelo'}
            />
          </Box>
        </TabPanel>
        <TabPanel value="3" className={classes.configTabPanel}>
          <ConversationsTab
            setModalState={setModalState}
            sectorId={sectorId}
            chartName={SOIL_MOISTURE_NAME}
            refreshCounter={refreshCounter}
          />
        </TabPanel>
      </TabContext>
      <AlertModal
        interval={interval}
        open={alertModalOpen}
        onClose={() => setAlertModalOpen(false)}
        sectorId={sectorId}
        measurements={availableDepths.map((depth) => ({
          id: depth,
          name: depth,
        }))}
        chartName={'humedad-suelo'}
        onAlertUpdated={() => setAlertsRefreshCounter((prev) => prev + 1)}
      />
    </>
  );
};

export { EMPTY_MODAL_STATE };
export type { ModalState };
export default SoilMoistureConfigContent;
